<template>
  <div class="container">
    <h1 v-bg-typography="'h1'" :style="{ marginTop: '10px' }">
      Qualitative feedback
    </h1>
    <div class="bg">
      <div class="bg-meta">
        <h2 v-bg-typography="'h2'" :class="['title-white']">Associations</h2>
        <div class="explanation-subtitle">
          Strongest associations related to the video as chosen by the
          participants
        </div>
      </div>
      <div
        :class="
          `associations-chart-container ${
            currentAssociationsPage === index
              ? ''
              : 'associations-chart-container--hidden'
          }`
        "
        v-for="(associationsData, index) in associationsPages"
        :key="`associations_graph_${index}`"
      >
        <bg-association-chart
          :height="200"
          :chartData="associationsData"
          :maxTicks="participantsCount"
        />
      </div>
      <div class="associations-chart-container__navigation">
        <a
          :class="
            `associations-chart-container__navigation__prev ${currentAssociationsPage ===
              0 && 'hidden'}`
          "
          href="#"
          @click.prevent="handlePrevAssociationsPage"
        >
          &larr; see previous 10
        </a>
        <a
          :class="
            `associations-chart-container__navigation__next ${currentAssociationsPage +
              1 ===
              associationsPagesCount && 'hidden'}`
          "
          href="#"
          @click.prevent="handleNextAssociationsPage"
        >
          see next 10 &rarr;
        </a>
      </div>
    </div>
    <div class="bg">
      <div class="bg-meta">
        <h2 v-bg-typography="'h2'" :class="['title-white']">Evaluations</h2>
        <div class="explanation-subtitle">
          Average scores on five evaluation questions given by the participants
        </div>
      </div>
      <div class="radar-chart-container">
        <bg-radar-chart :chartData="radarData" />
      </div>
    </div>
    <div class="open-questions">
      <h2 v-bg-typography="'h2'">Open questions</h2>
      <bg-select
        @input="handleSelectChange"
        :options="questions"
        :value="currentQuestion"
        :extra-wide="true"
      ></bg-select>
      <h3 v-bg-typography="'h3'">Answers:</h3>
      <div class="detail-container" v-if="currentAnswers.length > 0">
        <bg-detail-card
          v-for="answer in currentAnswers"
          v-bind:key="answer.id"
          :text="answer.text"
          detail="Answer"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$green: var(--bg-color-green);
$pink: var(--bg-color-pink);
$yellow: var(--bg-color-yellow);
.legend {
  flex-direction: row-reverse;
  padding-bottom: 20px;
}

.bg {
  margin-top: 40px;
  background-color: var(--bg-color-gray-dark);
  margin-bottom: 40px;
  border-radius: var(--bg-border-radius);
  position: relative;
  padding: 40px;
}

.container {
  max-width: 1100px;
}

.video-container {
  position: relative;
}

.bg-meta {
  color: white;
}

.radar-chart-container {
  max-width: 600px;
  max-height: 500px;
  margin-right: auto;
  margin-left: auto;
  margin-top: -80px;
}
.associations-chart-container {
  position: relative;
  margin-top: 40px;
  transition: height 2s ease-in-out 1s;
}
.explanation-subtitle {
  margin-top: -15px;
}
.associations-chart-container--hidden {
  height: 0;
  overflow: hidden;
  margin-top: 0px;
}
.associations-chart-container__navigation {
  padding-top: 35px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  & > .associations-chart-container__navigation__prev,
  & > .associations-chart-container__navigation__next {
    text-decoration: underline;
    color: #fff;
    &.hidden {
      display: none;
    }
  }
  & > .associations-chart-container__navigation__next {
    margin-left: auto;
    order: 2;
  }
}
.title-white {
  color: #fff;
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgAssociationChart from "@/components/bg-association-chart";
import BasePageTitle from "@/components/base-page-title";
import BgRadarChart from "@/components/bg-radar-chart";
import BgSelect from "@/components/bg-select";
import BgDetailCard from "@/components/bg-detail-card";
import API from "@/api";
import { mapState } from "vuex";
export default {
  name: "overview",
  components: {
    BgAssociationChart,
    BgRadarChart,
    BgSelect,
    BgDetailCard,
    // BasePageTitle,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      questions: [],
      currentQuestion: {},
      currentAnswers: [],
      participantsCount: 0,
      associationsPages: [],
      associationsPagesCount: 0,
      currentAssociationsPage: 0,
    };
  },
  computed: {
    ...mapState({
      testState: (state) => state.test,
    }),
    radarData() {
      const evals = this.testState.evaluations;
      let labels = [];
      const dataset = evals.map((e) => {
        const sum = e.answers.reduce((a, b) => a + parseFloat(b.score), 0);
        let result = 0;
        if (e.answers.length !== 0) {
          result = (sum / e.answers.length).toFixed(2);
        }
        labels.push(`${e.text}: ${result}`);
        return result;
      });
      return {
        labels: labels,
        datasets: [
          {
            data: dataset,
            borderColor: "#fff",
            // pointBackgroundColor is defined below; remove duplicate
            backgroundColor: "rgba(255,255,255,0.2)",
            pointLabelFontColor: "#fff",
            radius: 6,
            pointRadius: 6,
            pointBorderWidth: 3,
            pointBackgroundColor: "#fff",
            pointBorderColor: "#fff",
            pointHoverRadius: 10,
            labels: {
              fontColor: "#fff",
            },
          },
        ],
      };
    },
  },
  watch: {
    "$route.params.id": function(id) {
      this.fetchData();
    },
  },
  methods: {
    handleNextAssociationsPage() {
      if (this.currentAssociationsPage + 1 < this.associationsPagesCount) {
        this.currentAssociationsPage += 1;
      }
    },
    handlePrevAssociationsPage() {
      if (this.currentAssociationsPage > 0) {
        this.currentAssociationsPage -= 1;
      }
    },
    handleSelectChange(value) {
      const id = value.value;
      this.currentAnswers = this.testState.open_questions.find(
        (q) => q.id === id
      ).answers;
    },
    fetchData() {
      let fakeId;
      if (this.testState.participants.find((p) => p.type === "video_heatmap")) {
        fakeId = this.testState.participants.find(
          (p) => p.type === "video_heatmap"
        ).id;
      }
      const testId = this.$route.params.id;
      this.participantsCount = this.testState.participants.filter(
        (p) => p.type === "default"
      ).length;
      API.test.getFakeParticipant(testId, fakeId).then((res) => {
        this.testData = {
          ...res.data,
        };
        this.questions = this.testState.open_questions.map((q) => {
          return {
            value: q.id,
            label: q.text,
          };
        });
        this.currentQuestion = {
          value: this.testState.open_questions[0].id,
          label: this.testState.open_questions[0].text,
        };
        this.currentAnswers = this.testState.open_questions[0].answers;
      });
    },
    setAssociationsPages() {
      const associations = this.testState.associations;
      const sortedAssociations = associations.sort(
        (a, b) => b.answers.length - a.answers.length
      );
      let startSlice = 0;
      let endSlice = 10;
      let resultingDatasets = [];

      while (true) {
        const slicedPage = sortedAssociations.slice(startSlice, endSlice);
        if (slicedPage.length === 0) {
          break;
        }

        const dataset = {
          backgroundColor: "#fff",
          data: slicedPage.map((a) => a.answers.length),
        };

        resultingDatasets.push({
          datasets: [dataset],
          labels: slicedPage.map((a) => a.text),
        });

        startSlice += 10;
        endSlice += 10;

        if (slicedPage.length < 10) {
          break;
        }
      }

      this.associationsPages = resultingDatasets;
      this.associationsPagesCount = resultingDatasets.length;
    },
  },
  created() {
    this.fetchData();
    this.setAssociationsPages();
  },
};
</script>
